import { Link, useNavigate } from "react-router-dom";
import { useCart, lineTotals, memberSavings } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl, fmtMoney } from "@/lib/api";
import { Plus, Minus, X, ShoppingBag, ArrowRight, BadgeCheck } from "lucide-react";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, savings } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const memberDiscount = memberSavings(items, !!user);
  const estimatedTotal = +(subtotal - memberDiscount).toFixed(2);

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-20" data-testid="empty-cart">
        <div className="text-center max-w-md mx-auto">
          <ShoppingBag className="w-12 h-12 mx-auto text-gray-300" />
          <h1 className="font-display font-black text-3xl tracking-tighter mt-6">
            Your cart is empty.
          </h1>
          <p className="text-gray-600 mt-3">
            Add some recliners, pallets, or e-bikes to get started.
          </p>
          <Link to="/shop" className="btn-primary inline-flex mt-8" data-testid="empty-cart-shop-btn">
            Start shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12" data-testid="cart-page">
      <div className="ipv-eyebrow text-[#0A1C40]">Cart</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
        Your cart
      </h1>

      <div className="mt-10 grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-6">
          {[
            { label: "From our store", note: "Pickup or local delivery · store return policy (exchange/store credit)", list: items.filter((i) => !i.ships_only), tid: "cart-group-store" },
            { label: "Ships to you", note: "Standard carrier shipping · mail returns, refund to original payment method", list: items.filter((i) => i.ships_only), tid: "cart-group-ships" },
          ].filter((g) => g.list.length > 0).map((group, gi, arr) => (
            <div key={group.tid} data-testid={group.tid}>
              {(arr.length > 1 || group.tid === "cart-group-ships") && (
                <div className="mb-2">
                  <div className="font-display font-black text-sm tracking-wide uppercase text-[#0A1C40]">{group.label}</div>
                  <div className="text-[11px] text-gray-500">{group.note}</div>
                </div>
              )}
              <div className="border border-gray-200 divide-y divide-gray-200">
                {group.list.map((it) => {
            const lt = lineTotals(it);
            return (
            <div key={it.key} className="p-4 sm:p-5 flex flex-wrap sm:flex-nowrap gap-4 sm:gap-5 items-center" data-testid={`cart-row-${it.product_id}`}>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#F4F4F5] border border-gray-200 shrink-0 overflow-hidden">
                {it.image && (
                  <img src={fileUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold leading-snug">{it.name}</div>
                {it.color && (
                  <div className="text-xs font-bold text-[#0A1C40] mt-0.5" data-testid={`cart-color-${it.product_id}`}>
                    Color: {it.color}
                  </div>
                )}
                {it.pallet_names && (
                  <div className="text-xs text-gray-600 mt-1" data-testid="cart-pallet-contents">
                    {it.pallet_names.join(" · ")}
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-1">{fmtMoney(it.price)}</div>
                {it.fulfillment_type === "order_in" && (
                  <div className="text-xs mt-1 font-bold text-[#0A1C40]" data-testid={`cart-special-${it.product_id}`}>
                    📦 Special order · ready in ~{it.lead_time_days || 7} days
                  </div>
                )}
                {it.bulk_deal && it.bulk_deal.min_qty >= 2 && (
                  <div className={`text-xs mt-1 font-bold ${lt.dealActive ? "text-green-700" : "text-[#C99700]"}`}>
                    {lt.dealActive
                      ? `Deal applied — you save ${fmtMoney(lt.savings)}!`
                      : `Buy ${it.bulk_deal.min_qty}+ to save ${it.bulk_deal.percent_off}%`}
                  </div>
                )}
              </div>
              {/* On phones this group wraps to its own full-width row */}
              <div className="flex items-center justify-between gap-3 w-full sm:w-auto sm:contents">
                <div className="flex items-center border border-gray-300">
                  <button type="button" data-testid={`cart-qty-dec-${it.product_id}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2.5 [touch-action:manipulation]">
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <div className="w-9 text-center text-sm font-bold">{it.quantity}</div>
                  <button type="button" data-testid={`cart-qty-inc-${it.product_id}`} onClick={() => updateQty(it.key, it.quantity + 1)} className="p-2.5 [touch-action:manipulation]">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="font-display font-bold sm:w-24 text-right">
                  {fmtMoney(lt.total)}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(it.key)}
                  className="p-2.5 text-gray-400 hover:text-[#DC2626] [touch-action:manipulation]"
                  data-testid={`cart-remove-${it.product_id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
                })}
              </div>
            </div>
          ))}
          {items.some((i) => i.ships_only) && items.some((i) => !i.ships_only) && (
            <div className="text-xs bg-blue-50 border border-[#0A1C40]/30 px-3 py-2 text-[#0A1C40] font-semibold" data-testid="cart-mixed-notice">
              Items in this order may arrive separately. Store pickup and local delivery apply only to items marked "Available in store now."
            </div>
          )}
        </div>

        <aside className="border border-gray-200 p-6 h-fit sticky top-28" data-testid="cart-summary">
          <div className="ipv-eyebrow">Order summary</div>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-bold" data-testid="cart-subtotal">{fmtMoney(subtotal)}</span>
            </div>
            {savings > 0 && (
              <div className="flex justify-between text-green-700 font-bold" data-testid="cart-savings">
                <span>You&apos;re saving</span>
                <span>{fmtMoney(savings)}</span>
              </div>
            )}
            {user && memberDiscount > 0 && (
              <div className="flex justify-between text-green-700 font-bold" data-testid="cart-member-savings">
                <span className="inline-flex items-center gap-1">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Member 10% off
                </span>
                <span>-{fmtMoney(memberDiscount)}</span>
              </div>
            )}
            {!user && (
              <div className="text-xs bg-[#0A1C40] text-white px-3 py-2" data-testid="cart-member-cta">
                <Link to="/login" className="font-bold underline">Sign in</Link>{" "}
                to save an extra <span className="text-[#FFC220] font-bold">10% off</span> on non-pallet items.
              </div>
            )}
            <div className="flex justify-between text-gray-500">
              <span>Tax & delivery</span>
              <span>Calculated at checkout</span>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-between items-baseline">
            <div className="font-bold">Total</div>
            <div className="font-display font-black text-2xl" data-testid="cart-total">{fmtMoney(estimatedTotal)}</div>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
            data-testid="checkout-btn"
          >
            Proceed to checkout <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/shop" className="block text-center text-sm text-gray-600 hover:text-black mt-4">
            Continue shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
